// Pure CRM helpers for the SPA (spec 2026-11-03): status/priority display, overdue, counts,
// sorting, search and duplicate detection. Dates are "YYYY-MM-DD" strings.

export const STATUS_ORDER = ["lead", "in_progress", "won", "gone_cold", "not_interested"];
export const OPEN_STATUSES = ["lead", "in_progress"];
export const STATUS_META = {
  lead: { label: "Lead", color: "#cbd5e1", bg: "rgba(148,163,184,0.15)" },
  in_progress: { label: "In Progress", color: "#93c5fd", bg: "rgba(59,130,246,0.15)" },
  won: { label: "Won", color: "#6ee7b7", bg: "rgba(16,185,129,0.15)" },
  gone_cold: { label: "Gone Cold", color: "#fcd34d", bg: "rgba(245,158,11,0.15)" },
  not_interested: { label: "Not Interested", color: "#fca5a5", bg: "rgba(239,68,68,0.12)" },
};
export const PRIORITY_ORDER = ["high", "med", "low"];
export const PRIORITY_META = {
  high: { label: "High", color: "#ef4444" },
  med: { label: "Med", color: "#f59e0b" },
  low: { label: "Low", color: "#64748b" },
};

// "Working it" can be the client itself (they're leading the conversation) — stored as this
// sentinel in ownerUserId, like the "client" assignee on tasks.
export const CLIENT_OWNER = "client";
export const isClientOwner = (id) => id === CLIENT_OWNER;

export const isOpen = (t) => OPEN_STATUSES.includes(t?.status);
export const isOverdue = (t, today) => isOpen(t) && !!t.nextStepDate && t.nextStepDate < today;

export function trackCounts(tracks, today) {
  const c = { contacts: 0, lead: 0, in_progress: 0, won: 0, gone_cold: 0, not_interested: 0, overdue: 0 };
  for (const t of tracks || []) {
    c.contacts += 1;
    if (t.status in c) c[t.status] += 1;
    if (isOverdue(t, today)) c.overdue += 1;
  }
  return c;
}

// Overdue first, then priority high→low, then next-step date (none last), then contact name.
// Tracks don't carry the contact's name — `nameOf(track)` supplies it.
export function sortTracks(tracks, today, nameOf = () => "") {
  const pri = (t) => { const i = PRIORITY_ORDER.indexOf(t.priority); return i < 0 ? 1 : i; };
  return [...(tracks || [])].sort((a, b) => {
    const oa = isOverdue(a, today) ? 0 : 1, ob = isOverdue(b, today) ? 0 : 1;
    if (oa !== ob) return oa - ob;
    if (pri(a) !== pri(b)) return pri(a) - pri(b);
    const da = a.nextStepDate || "9999-99-99", db = b.nextStepDate || "9999-99-99";
    if (da !== db) return da < db ? -1 : 1;
    return String(nameOf(a) || "").localeCompare(String(nameOf(b) || ""));
  });
}

export function searchContacts(contacts, query) {
  const q = String(query || "").trim().toLowerCase();
  if (!q) return [...(contacts || [])];
  return (contacts || []).filter((c) =>
    [c.name, c.organization, c.role, c.base, ...(c.expertise || [])].some((v) => String(v || "").toLowerCase().includes(q)));
}

export function clientsByContact(tracks) {
  const m = new Map();
  for (const t of tracks || []) {
    const list = m.get(t.contactId) || [];
    if (!list.includes(t.clientId)) list.push(t.clientId);
    m.set(t.contactId, list);
  }
  return m;
}

// Leading rank/honorific tokens ignored when comparing names (two-word ranks listed first).
const RANKS = ["lt gen", "maj gen", "brig gen", "lt col", "gen", "col", "maj", "capt", "cpt", "1lt", "2lt", "lt", "cdr", "lcdr",
  "adm", "radm", "vadm", "dr", "mr", "ms", "mrs", "sgt", "msgt", "smsgt", "cmsgt"];

export function normalizeName(name) {
  let n = String(name || "").toLowerCase().replace(/[.,]/g, " ").replace(/\s+/g, " ").trim();
  let changed = true;
  while (changed) {
    changed = false;
    for (const r of RANKS) {
      if (n.startsWith(r + " ")) { n = n.slice(r.length + 1); changed = true; break; }
    }
  }
  return n;
}
const normEmail = (e) => String(e || "").trim().toLowerCase();

export function findDuplicates(contacts, { name, email } = {}, exceptId = null) {
  const e = normEmail(email);
  const n = normalizeName(name);
  const others = (contacts || []).filter((c) => c.id !== exceptId);
  return {
    emailMatch: (e && others.find((c) => normEmail(c.email) === e)) || null,
    nameMatches: n ? others.filter((c) => normalizeName(c.name) === n) : [],
  };
}
